import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const AuthContext = createContext();
const API_BASE = import.meta.env.VITE_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    plan: '',
    darkMode: false,
    language: 'pt',
    mfaEnabled: false,
    createdAt: '',
    status: '',
    subscription: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Não busca user-info na tela de login
    if (
  location.pathname === '/login' ||
  location.pathname === '/register' ||
  location.pathname === '/reset-password' ||
  location.pathname.startsWith('/share/')
) {
  setLoading(false);
  return;
}

    async function fetchUser() {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        setUserInfo({
          username: '',
          email: '',
          firstName: '',
          lastName: '',
          plan: '',
          darkMode: false,
          language: 'pt',
          mfaEnabled: false,
          createdAt: '',
          status: '',
          subscription: ''
        });
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/user-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          // Token inválido ou expirado
          localStorage.removeItem('token');
          setUserInfo({
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            plan: '',
            darkMode: false,
            language: 'pt',
            mfaEnabled: false,
            createdAt: '',
            status: '',
            subscription: ''
          });
          setLoading(false);
          navigate('/login');
          return;
        }

        if (!res.ok) {
          // Erro 500, 404, etc.
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setUserInfo({
          username:    data.username || '',
          email:       data.email || '',
          firstName:   data.firstName || '',
          lastName:    data.lastName || '',
          plan:        data.plan || '',
          darkMode:    data.darkMode ?? false,
          language:    data.language ?? 'pt',
          mfaEnabled:  data.mfaEnabled ?? false,
          createdAt:   data.createdAt || '',
          status:      data.status || '',
          subscription: data.subscription || ''
        });
      } catch (err) {
        console.error('Falha ao carregar user-info:', err);
        localStorage.removeItem('token');
        setUserInfo({
          username: '',
          email: '',
          firstName: '',
          lastName: '',
          plan: '',
          darkMode: false,
          language: 'pt',
          mfaEnabled: false,
          createdAt: '',
          status: '',
          subscription: ''
        });
        navigate('/login');
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
    // eslint-disable-next-line
  }, [location.pathname]);

  const updateUser = async (updates) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token ausente');

    try {
      const res = await fetch(`${API_BASE}/api/user-info`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        throw new Error(`Erro ao atualizar usuário: HTTP ${res.status}`);
      }
      const updated = await res.json();
      setUserInfo(prev => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('Erro no updateUser:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ userInfo, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro do AuthProvider');
  return ctx;
};
